import Filter from './Filter.js';
import { addUnits, isNumeric } from './helpers.js';
import { ChangeEvent } from './ChangeEvent.js';

/**
 * Handles Dropdown creation
 */
class Dropdown extends Filter {
  constructor(containerId, searchParameter, options, updateCallback, searchParameters) {
    super({ containerId, searchParameter, options, updateCallback, searchParameters });
    this.container = document.getElementById(containerId);
    this.placeholder = this.container.dataset.placeholder;
    this.key = this.container.dataset.key;
    this.ui = this.container.dataset.ui;
  }

  create() {
    let id, valuesRange;

    // Clear the existing widget.
    this.container.innerHTML = '';

    this.container.classList.add('select-box');

    if (this.ui === 'split' && isNumeric(this.searchParameter)) {
      let selectRow = document.createElement('div');
      let minContainer = document.createElement('div');
      let maxContainer = document.createElement('div');
      let minDropdown = document.createElement('select');
      let maxDropdown = document.createElement('select');
      let minLabel = document.createElement('label');
      let maxLabel = document.createElement('label');

      valuesRange = this.setRangeValues();
      selectRow.classList.add('row');
      minContainer.classList.add('col-6');
      maxContainer.classList.add('col-6');
      minLabel.innerText = 'Min';
      maxLabel.innerText = 'Max';

      this.container.appendChild(selectRow);

      minContainer.appendChild(minLabel);
      maxContainer.appendChild(maxLabel);
      selectRow.appendChild(minContainer);
      selectRow.appendChild(maxContainer);

      minContainer.appendChild(minDropdown);
      maxContainer.appendChild(maxDropdown);

      this.createIndividualDropdowns(minDropdown, 'min', valuesRange);
      this.createIndividualDropdowns(maxDropdown, 'max', valuesRange);

      return;
    }

    const dropdown = document.createElement('select');
    dropdown.classList.add(`${this.searchParameter}FilterDropdown`);
    // This ID should be different, but it might be in use.
    dropdown.id = `dsp_${this.containerId}`;
    dropdown.name = `dsp_${this.searchParameter}[]`;
    this.container.appendChild(dropdown);

    this.setEmptyOption(dropdown);

    if (isNumeric(this.searchParameter)) {
      valuesRange = this.setRangeValues();

      for (let i = this.options.minValue; i <= this.options.maxValue; i += this.options.step) {
        let htmlOption = document.createElement('option');
        htmlOption.innerText = `${addUnits(this.searchParameter, i)} - ${addUnits(
          this.searchParameter,
          i + this.options.step,
        )}`;
        htmlOption.dataset.min = i;
        htmlOption.dataset.max = i + this.options.step;

        if (
          parseInt(htmlOption.dataset.max) == valuesRange['minValue'] &&
          parseInt(htmlOption.dataset.min) == valuesRange['maxValue']
        ) {
          htmlOption.toggleAttribute('selected', true);
        }

        dropdown.appendChild(htmlOption);
      }

      return;
    }

    if (this.searchParameters[this.searchParameter]) {
      let valuesArray = this.searchParameters[this.searchParameter].split(',');
      if (valuesArray.length === 1) {
        id = valuesArray[0];
      }
    }

    if (!this.options) {
      this.container.dataset.optionCount = 0;
      return;
    }

    let optionCount = 0;
    this.options.forEach((option) => {
      const optionKey = this.getOptionKey(option);
      const htmlOption = document.createElement('option');
      let innerText = '';

      if (option.level === 0) {
        htmlOption.toggleAttribute('disabled', true);
        innerText = option.name /*+ ' (' + option.doc_count + ')'*/;
      } else {
        htmlOption.value = optionKey;

        if (this.searchParameter === 'category') {
          innerText = '- ';
          htmlOption.classList.add('pl-3');
        }

        if (this.searchParameter == 'ext_colour' || this.searchParameter == 'int_colour') {
          innerText += `${option.id} (${option.doc_count})`;
        } else {
          innerText += `${option.name}`;
          if (!!option.doc_count) {
            innerText += ` (${option.doc_count})`;
          }
        }

        if (option.name && !option.name.includes('(not specified)')) {
          htmlOption.setAttribute('data-option-name', option.name);
        }

        optionCount += 1;
      }

      if (typeof id !== 'undefined') {
        if (optionKey == id) {
          htmlOption.toggleAttribute('selected', true);
        }

        let shouldSelect = parseInt(id) == NaN ? false : option.id == id;

        if (typeof option.id !== 'undefined' && shouldSelect) {
          htmlOption.toggleAttribute('selected', true);
        }
      }

      htmlOption.innerText = innerText;
      dropdown.appendChild(htmlOption);
    });

    this.container.dataset.optionCount = optionCount;
  }

  update() {
    const filter = this;
    const dropdowns = filter.container.querySelectorAll(`.${filter.searchParameter}FilterDropdown`);
    const searchParameter = filter.searchParameter;
    const searchParameters = filter.searchParameters;

    dropdowns.forEach((dropdown) => {
      dropdown.addEventListener('change', function (e) {
        if (isNumeric(searchParameter) && filter.ui === 'split') {
          searchParameters[`${searchParameter}_min`] =
            dropdown.getAttribute('name') === `dsp_${searchParameter}_min`
              ? dropdown.querySelector('option:checked').dataset.min
              : searchParameters[`${searchParameter}_min`];
          searchParameters[`${searchParameter}_max`] =
            dropdown.getAttribute('name') === `dsp_${searchParameter}_max`
              ? dropdown.querySelector('option:checked').dataset.max
              : searchParameters[`${searchParameter}_max`];
        } else if (isNumeric(searchParameter)) {
          searchParameters[`${searchParameter}_min`] =
            dropdown.querySelector('option:checked').dataset.min;
          searchParameters[`${searchParameter}_max`] =
            dropdown.querySelector('option:checked').dataset.max;
        } else {
          searchParameters[searchParameter] = dropdown.querySelector('option:checked').value;
        }

        filter.container.dispatchEvent(new ChangeEvent(e.target, e));

        if (filter.updateCallback && typeof filter.updateCallback === 'function') {
          filter.updateCallback(searchParameter);
        }
      });
    });
  }

  setEmptyOption(dropdown) {
    let htmlOption = document.createElement('option');
    htmlOption.value = '';
    htmlOption.innerText = this.placeholder ? this.placeholder : 'Any';
    dropdown.appendChild(htmlOption);
  }

  getOptionKey(option) {
    let key = '';
    if (typeof this.key !== 'undefined') {
      key = option[this.key];
    } else if (this.searchParameter === 'transmission_type') {
      key = option.name ?? option.id;
    } else {
      key = option.slug ?? option.id ?? option.name;
    }

    // The 'other-unsure' slug often does not work.
    if (key === 'other-unsure' && option.id) {
      key = option.id;
    }

    return key;
  }

  setRangeValues() {
    let minValue = this.searchParameters[`${this.searchParameter}_min`]
      ? parseInt(this.searchParameters[`${this.searchParameter}_min`])
      : this.options.minValue;

    let maxValue = this.searchParameters[`${this.searchParameter}_max`]
      ? parseInt(this.searchParameters[`${this.searchParameter}_max`])
      : this.options.maxValue;

    return {
      minValue: minValue,
      maxValue: maxValue,
    };
  }

  createIndividualDropdowns(dropdown, type, valuesRange) {
    dropdown.id = `dsp_${this.searchParameter}_${type}`;
    dropdown.name = `dsp_${this.searchParameter}_${type}`;
    dropdown.classList.add('select-box');
    dropdown.classList.add(`${this.searchParameter}FilterDropdown`);
    this.setEmptyOption(dropdown);

    for (let i = this.options.minValue; i <= this.options.maxValue; i += this.options.step) {
      let htmlOption = document.createElement('option');
      htmlOption.innerText = `${addUnits(this.searchParameter, i)}`;
      htmlOption.dataset[`${type}`] = i;

      if (
        this.searchParameters[`${this.searchParameter}_${type}`] &&
        parseInt(htmlOption.dataset[`${type}`]) == valuesRange[`${type}Value`]
      ) {
        htmlOption.toggleAttribute('selected', true);
      }

      dropdown.appendChild(htmlOption);
    }
  }
}

export default Dropdown;
